'use client';

import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { validatePassword, type PasswordValidation } from '@/lib/utils/password';
import { useI18n } from '@/contexts/I18nContext';

interface PasswordStrengthIndicatorProps {
  password: string;
  className?: string;
}

export function PasswordStrengthIndicator({
  password,
  className,
}: PasswordStrengthIndicatorProps) {
  const { t } = useI18n();
  const validation: PasswordValidation = validatePassword(password);

  if (!password) {
    return null;
  }

  const requirements = [
    {
      label: t('auth.password.minLength'),
      met: validation.requirements.minLength,
    },
    {
      label: t('auth.password.uppercase'),
      met: validation.requirements.hasUppercase,
    },
    {
      label: t('auth.password.number'),
      met: validation.requirements.hasNumber,
    },
    {
      label: t('auth.password.special'),
      met: validation.requirements.hasSpecialChar,
    },
  ];

  return (
    <div className={cn('space-y-2', className)}>
      <p className="text-xs font-medium text-ink-500">{t('auth.password.requirements')}</p>
      <div className="space-y-1">
        {requirements.map((req, index) => (
          <div
            key={index}
            className={cn(
              'flex items-center gap-2 text-xs transition-all duration-200',
              req.met ? 'text-emerald-600' : 'text-ink-400'
            )}
          >
            <div
              className={cn(
                'flex items-center justify-center w-4 h-4 rounded-full transition-all duration-200',
                req.met
                  ? 'bg-emerald-100 text-emerald-600'
                  : 'bg-ink-100 text-ink-400'
              )}
            >
              {req.met ? (
                <Check className="w-3 h-3" strokeWidth={3} />
              ) : (
                <X className="w-3 h-3" strokeWidth={2} />
              )}
            </div>
            <span className={cn(req.met && 'font-medium')}>{req.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
